import { useState, useEffect, useRef, useCallback } from "react";
import * as faceapi from "face-api.js";

const THROTTLE_MS = 150;
const AUTO_CAPTURE_DELAY = 1800;
const MODEL_URL = "/models/face-api";

const FACE_SIZE_MIN = 0.18;
const FACE_SIZE_MAX = 0.85;
const CENTER_THRESHOLD = 0.18;
const YAW_LIMIT = 25;
const PITCH_LIMIT = 30;
const ROLL_LIMIT = 25;
const LUMA_MEAN_MIN = 10;
const LUMA_MEAN_MAX = 250;
const LUMA_STD_MIN = 5;
const DARK_ROOM_THRESHOLD = 30;

const defaultGatingState = {
  facePresent: false,
  faceSize: false,
  centering: false,
  pose: false,
  lighting: false,
};

function estimatePose(landmarks) {
  const pts = landmarks.positions;
  // Roll: angle between outer eye corners (landmarks 36, 45)
  const leftEye = pts[36];
  const rightEye = pts[45];
  const roll =
    Math.abs(
      Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x) *
        (180 / Math.PI)
    );

  // Yaw: nose tip (30) deviation from face midline (0=jaw left, 16=jaw right)
  const noseTip = pts[30];
  const jawLeft = pts[0];
  const jawRight = pts[16];
  const faceMidX = (jawLeft.x + jawRight.x) / 2;
  const faceWidth = Math.abs(jawRight.x - jawLeft.x);
  const yaw = faceWidth > 0
    ? Math.abs(((noseTip.x - faceMidX) / faceWidth) * 120)
    : 0;

  // Pitch: nose tip vertical position relative to eye-chin range
  // Nose TIP (landmark 30) sits at ~35% of eye-to-chin distance in frontal view
  const eyeMidY = (leftEye.y + rightEye.y) / 2;
  const chin = pts[8];
  const eyeChinDist = chin.y - eyeMidY;
  const noseFromEyes = noseTip.y - eyeMidY;
  const expectedRatio = 0.35;
  const actualRatio = eyeChinDist > 0 ? noseFromEyes / eyeChinDist : 0.5;
  const pitch = Math.abs((actualRatio - expectedRatio) * 120);

  return { yaw, pitch, roll };
}

function analyzeLighting(video, box) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const x = Math.max(0, Math.round(box.x));
  const y = Math.max(0, Math.round(box.y));
  const w = Math.min(Math.round(box.width), video.videoWidth - x);
  const h = Math.min(Math.round(box.height), video.videoHeight - y);
  if (w <= 0 || h <= 0) return { mean: 0, stdDev: 0 };

  canvas.width = w;
  canvas.height = h;
  ctx.drawImage(video, x, y, w, h, 0, 0, w, h);
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;

  let sum = 0;
  let count = 0;
  const lumaValues = [];
  for (let i = 0; i < data.length; i += 4) {
    const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    lumaValues.push(luma);
    sum += luma;
    count++;
  }

  const mean = count > 0 ? sum / count : 0;
  let variance = 0;
  for (let i = 0; i < lumaValues.length; i++) {
    variance += (lumaValues[i] - mean) ** 2;
  }
  const stdDev = count > 0 ? Math.sqrt(variance / count) : 0;

  return { mean, stdDev };
}

function analyzeFrameBrightness(video) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const sw = 64;
  const sh = 48;
  canvas.width = sw;
  canvas.height = sh;
  ctx.drawImage(video, 0, 0, sw, sh);
  const imageData = ctx.getImageData(0, 0, sw, sh);
  const data = imageData.data;
  let sum = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 4) {
    sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    count++;
  }
  return count > 0 ? sum / count : 0;
}

function getFeedbackKey(gating, isTooDark) {
  if (!gating.facePresent) {
    return isTooDark ? "tooDark" : "positionFace";
  }
  if (!gating.faceSize) return "adjustDistance";
  if (!gating.centering) return "centerFace";
  if (!gating.pose) return "lookStraight";
  if (!gating.lighting) {
    return isTooDark ? "tooDark" : "improveLighting";
  }
  return "holdStill";
}

export default function useFaceDetection(videoRef, active) {
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [modelLoadError, setModelLoadError] = useState(null);
  const [gatingState, setGatingState] = useState(defaultGatingState);
  const [feedbackKey, setFeedbackKey] = useState("positionFace");
  const [isAllPassing, setIsAllPassing] = useState(false);
  const [isTooDark, setIsTooDark] = useState(false);

  const allPassingStartRef = useRef(null);
  const rafRef = useRef(null);
  const lastDetectRef = useRef(0);
  const onAutoCaptureRef = useRef(null);
  const frameChainRef = useRef([]);

  // Orientation telemetry — records device orientation during detection loop.
  // Real phones produce varying readings; scripted attacks produce none.
  const orientationRef = useRef([]);
  const orientationStartRef = useRef(null);

  // Load models
  useEffect(() => {
    let cancelled = false;
    async function loadModels() {
      try {
        console.log("[FaceDetect] Loading models from", MODEL_URL);
        await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
        console.log("[FaceDetect] TinyFaceDetector loaded");
        await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
        console.log("[FaceDetect] FaceLandmark68 loaded");
        if (!cancelled) setModelsLoaded(true);
      } catch (err) {
        console.error("[FaceDetect] Failed to load models:", err);
        if (!cancelled) setModelLoadError(err);
      }
    }
    loadModels();
    return () => {
      cancelled = true;
    };
  }, []);

  const setOnAutoCapture = useCallback((fn) => {
    onAutoCaptureRef.current = fn;
  }, []);

  const getFrameChain = useCallback(() => [...frameChainRef.current], []);

  // Detection loop
  useEffect(() => {
    if (!modelsLoaded || !active) {
      console.log("[FaceDetect] Loop not starting — modelsLoaded:", modelsLoaded, "active:", active);
      return;
    }

    const video = videoRef?.current?.video || videoRef?.current;
    if (!video) {
      console.log("[FaceDetect] No video element found. videoRef.current:", videoRef?.current);
      return;
    }

    console.log("[FaceDetect] Detection loop starting");
    frameChainRef.current = [];
    orientationRef.current = [];
    orientationStartRef.current = performance.now();
    let running = true;
    let loopCount = 0;

    // Orientation telemetry — collect device orientation samples during detection
    const orientationHandler = (e) => {
      if (orientationRef.current.length >= 30) return; // cap samples
      orientationRef.current.push({
        t: Math.round(performance.now() - (orientationStartRef.current || 0)),
        a: Math.round((e.alpha || 0) * 10) / 10,
        b: Math.round((e.beta || 0) * 10) / 10,
        g: Math.round((e.gamma || 0) * 10) / 10,
      });
    };
    window.addEventListener("deviceorientation", orientationHandler);

    const detectOptions = new faceapi.TinyFaceDetectorOptions({
      inputSize: 224,
      scoreThreshold: 0.5,
    });

    const detect = async () => {
      if (!running) return;

      const now = performance.now();
      if (now - lastDetectRef.current < THROTTLE_MS) {
        rafRef.current = requestAnimationFrame(detect);
        return;
      }
      lastDetectRef.current = now;

      if (video.readyState < 2) {
        if (loopCount % 20 === 0) {
          console.log("[FaceDetect] Video not ready, readyState:", video.readyState);
        }
        loopCount++;
        rafRef.current = requestAnimationFrame(detect);
        return;
      }

      try {
        const detections = await faceapi
          .detectAllFaces(video, detectOptions)
          .withFaceLandmarks();

        const frameH = video.videoHeight;
        const frameW = video.videoWidth;
        const newGating = { ...defaultGatingState };

        // Check overall frame brightness for dark room detection
        const frameBrightness = analyzeFrameBrightness(video);
        const tooDark = frameBrightness < DARK_ROOM_THRESHOLD;

        if (loopCount % 10 === 0) {
          console.log("[FaceDetect] Frame:", frameW, "x", frameH, "| Faces found:", detections.length, "| Brightness:", frameBrightness.toFixed(1));
        }

        // Gate 1: exactly 1 face
        if (detections.length === 1) {
          newGating.facePresent = true;
          const det = detections[0];
          const box = det.detection.box;

          // Gate 2: face size (25-60% of frame height)
          const sizeRatio = box.height / frameH;
          newGating.faceSize =
            sizeRatio >= FACE_SIZE_MIN && sizeRatio <= FACE_SIZE_MAX;

          // Gate 3: centering (face center within 10% of frame center)
          const faceCenterX = box.x + box.width / 2;
          const faceCenterY = box.y + box.height / 2;
          const frameCenterX = frameW / 2;
          const frameCenterY = frameH / 2;
          const xOff = Math.abs(faceCenterX - frameCenterX) / frameW;
          const yOff = Math.abs(faceCenterY - frameCenterY) / frameH;
          newGating.centering =
            xOff <= CENTER_THRESHOLD && yOff <= CENTER_THRESHOLD;

          // Gate 4: pose
          const { yaw, pitch, roll } = estimatePose(det.landmarks);
          newGating.pose =
            yaw < YAW_LIMIT && pitch < PITCH_LIMIT && roll < ROLL_LIMIT;

          // Gate 5: lighting
          const { mean, stdDev } = analyzeLighting(video, box);
          newGating.lighting =
            mean >= LUMA_MEAN_MIN &&
            mean <= LUMA_MEAN_MAX &&
            stdDev >= LUMA_STD_MIN;

          if (loopCount % 10 === 0) {
            console.log("[FaceDetect] Gates:", {
              facePresent: newGating.facePresent,
              faceSize: `${newGating.faceSize} (ratio: ${sizeRatio.toFixed(3)}, need ${FACE_SIZE_MIN}-${FACE_SIZE_MAX})`,
              centering: `${newGating.centering} (xOff: ${xOff.toFixed(3)}, yOff: ${yOff.toFixed(3)}, limit: ${CENTER_THRESHOLD})`,
              pose: `${newGating.pose} (yaw: ${yaw.toFixed(1)}/${YAW_LIMIT}, pitch: ${pitch.toFixed(1)}/${PITCH_LIMIT}, roll: ${roll.toFixed(1)}/${ROLL_LIMIT})`,
              lighting: `${newGating.lighting} (mean: ${mean.toFixed(1)}, stdDev: ${stdDev.toFixed(1)})`,
            });
          }
        }

        // Record frame chain entry for capture integrity
        frameChainRef.current.push({
          t: Math.round(now),
          d: detections.length === 1 ? 1 : 0,
          g: Object.values(newGating).filter(Boolean).length,
        });
        if (frameChainRef.current.length > 200) frameChainRef.current.shift();

        const allPassing = Object.values(newGating).every(Boolean);

        setGatingState(newGating);
        setIsTooDark(tooDark);
        setFeedbackKey(getFeedbackKey(newGating, tooDark));
        setIsAllPassing(allPassing);

        // Auto-capture state machine
        if (allPassing) {
          if (!allPassingStartRef.current) {
            allPassingStartRef.current = performance.now();
            console.log("[FaceDetect] All gates PASSING — starting 1800ms timer");
          } else {
            const elapsed = performance.now() - allPassingStartRef.current;
            if (elapsed >= AUTO_CAPTURE_DELAY) {
              console.log("[FaceDetect] Auto-capture triggered! Elapsed:", elapsed.toFixed(0), "ms, callback exists:", !!onAutoCaptureRef.current);
              if (onAutoCaptureRef.current) {
                running = false;
                onAutoCaptureRef.current();
                return;
              }
            }
          }
        } else {
          if (allPassingStartRef.current) {
            console.log("[FaceDetect] Gates broke — timer reset");
          }
          allPassingStartRef.current = null;
        }
      } catch (err) {
        console.error("[FaceDetect] Detection error:", err);
      }

      loopCount++;
      if (running) {
        rafRef.current = requestAnimationFrame(detect);
      }
    };

    rafRef.current = requestAnimationFrame(detect);

    return () => {
      running = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      window.removeEventListener("deviceorientation", orientationHandler);
    };
  }, [modelsLoaded, active, videoRef]);

  const getOrientationSamples = useCallback(() => [...orientationRef.current], []);

  return {
    modelsLoaded,
    modelLoadError,
    gatingState,
    feedbackKey,
    isAllPassing,
    isTooDark,
    setOnAutoCapture,
    getFrameChain,
    getOrientationSamples,
  };
}
